import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { authService, profileService } from 'services/auth/authService';
import avatar from 'assets/images/avatars/avatar.svg';
import { toast } from 'react-toastify';
import OTPModal from 'components/auth/OTPModal';
import Spinner from 'components/common/Spinner';
import imageCompression from 'browser-image-compression';
import { AxiosResponse } from 'axios';
import { useTranslation } from 'react-i18next';
import { useTheme, ThemeMode } from 'contexts/ThemeContext';
import { useSidebar } from 'contexts/SidebarContext';
import { clearAccessToken, clearRefreshToken } from 'utils/auth/auth';
import {
  Settings as SettingsIcon,
  User,
  Shield,
  Globe,
  Sun,
  Moon,
  Monitor,
  Pencil,
  Check,
  X,
  Trash2,
  Camera,
  AlertTriangle,
  CheckCircle,
  Clock,
  Info,
  Menu,
} from 'lucide-react';

// Define interfaces for data structures
interface UserData {
  id: string;
  fullName: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  isPhoneVerified: boolean;
  isEmailVerified: boolean;
  is2FAEnabled: boolean;
  preferredLanguage: string;
  profilePicture: string | null;
  subscription: string;
  role: string;
}

interface FormData {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

interface EditMode {
  name: boolean;
  phone: boolean;
  password: boolean;
}

interface Profile {
  id?: string;
  full_name?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  phone_number?: string;
  is_phone_verified?: boolean;
  is_email_verified?: boolean;
  is_two_factor_enabled?: boolean;
  preferred_language?: string;
  profile_picture?: string | null;
  subscription?: string;
  role?: string;
  created_on?: string;
  two_factor_method?: string | null;
}

interface GenericAuthResponse<T = any> {
  status?: string;
  message?: string;
  data?: T;
}

// Define response types
type ProfileResponse = AxiosResponse<Profile | GenericAuthResponse<Profile>>;
type UploadPictureResponse = AxiosResponse<GenericAuthResponse<{ imageData: string }>>;

// Type guard to check if response is a Profile
const isProfile = (response: Profile | GenericAuthResponse<Profile>): response is Profile => {
  return 'email' in response || 'first_name' in response;
};

const Settings: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { isDark, themeMode, setThemeMode } = useTheme();
  const { toggleSidebar } = useSidebar();
  const [userData, setUserData] = useState<UserData>({
    id: '',
    fullName: t('settings.loading.text'),
    firstName: '',
    lastName: '',
    email: t('settings.loading.text'),
    phoneNumber: '',
    isPhoneVerified: false,
    isEmailVerified: false,
    is2FAEnabled: false,
    preferredLanguage: t('settings.labels.english'),
    profilePicture: null,
    subscription: '',
    role: '',
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [showFinalConfirmModal, setShowFinalConfirmModal] = useState<boolean>(false);
  const [subscriptionInfo, setSubscriptionInfo] = useState<{ daysRemaining: number; planName: string } | null>(null);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [showOTPModal, setShowOTPModal] = useState<boolean>(false);
  const [showPhoneOTPModal, setShowPhoneOTPModal] = useState<boolean>(false);
  const [isRequestingEmailOTP, setIsRequestingEmailOTP] = useState<boolean>(false);
  const [editMode, setEditMode] = useState<EditMode>({
    name: false,
    phone: false,
    password: false,
  });
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    phoneNumber: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  // Theme colors
  const containerBg = isDark ? '#1D293D' : '#F8FAFC';
  const mainBg = isDark ? '#0F172B' : '#FFFFFF';
  const mainShadow = isDark
    ? '0px 1px 2px rgba(2, 6, 24, 0.5)'
    : '0px 1px 2px rgba(43, 127, 255, 0.1)';
  const headerBorder = isDark ? '#1D293D' : '#F1F5F9';
  const titleColor = isDark ? '#E2E8F0' : '#314158';
  const subtitleColor = isDark ? '#90A1B9' : '#62748E';
  const cardBg = isDark ? '#0F172B' : '#FFFFFF';
  const cardBorder = isDark ? '#314158' : '#E2E8F0';
  const inputBg = isDark ? '#0F172B' : '#F8FAFC';
  const inputBorder = isDark ? '#314158' : '#E2E8F0';
  const inputColor = isDark ? '#E2E8F0' : '#314158';
  const sectionHeaderBg = isDark ? '#162032' : '#F8FAFC';
  const badgeBg = isDark ? '#162456' : '#EFF6FF';
  const badgeColor = isDark ? '#8EC5FF' : '#2B7FFF';
  const purpleBadgeBg = isDark ? '#3C0366' : '#FAF5FF';
  const purpleBadgeColor = isDark ? '#DAB2FF' : '#AD46FF';
  const greenBadgeBg = isDark ? '#022C22' : '#ECFDF5';
  const greenBadgeColor = isDark ? '#5EE9B5' : '#009966';
  const amberBadgeBg = isDark ? '#451A03' : '#FFFBEB';
  const amberBadgeColor = isDark ? '#FCD34D' : '#D97706';
  const dangerBg = isDark ? '#2D1215' : '#FEF2F2';
  const dangerBorder = isDark ? '#7F1D1D' : '#FECACA';
  const dangerHeaderBg = isDark ? '#3B1419' : '#FEF2F2';
  const modalBg = isDark ? '#1D293D' : '#FFFFFF';
  const modalHeaderBorder = isDark ? '#314158' : '#E2E8F0';

  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        setLoading(true);
        setError(null);
        const response: ProfileResponse = await profileService.getProfile();
        if (!response || !response.data) {
          throw new Error(t('settings.errors.invalidResponse'));
        }
        let profile: Profile;
        if (isProfile(response.data)) {
          profile = response.data;
        } else if (response.data.data && typeof response.data.data === 'object') {
          profile = response.data.data;
        } else {
          throw new Error(t('settings.errors.profileDataMissing'));
        }

        const fullName =
          profile.full_name || `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || t('settings.labels.user');

        const languageMap: { [key: string]: string } = {
          en: t('settings.labels.english'),
          fr: t('settings.labels.french'),
        };
        const preferredLang = profile.preferred_language || 'en';
        const displayLanguage = languageMap[preferredLang] || preferredLang;

        if (i18n.language !== preferredLang) {
          i18n.changeLanguage(preferredLang);
        }

        setUserData({
          id: profile.id || '',
          fullName,
          firstName: profile.first_name || '',
          lastName: profile.last_name || '',
          email: profile.email || t('settings.labels.noEmailProvided'),
          phoneNumber: profile.phone_number || '',
          isPhoneVerified: profile.is_phone_verified || false,
          isEmailVerified: profile.is_email_verified || false,
          is2FAEnabled: profile.is_two_factor_enabled || false,
          preferredLanguage: displayLanguage,
          profilePicture: profile.profile_picture || null,
          subscription: profile.subscription || t('settings.labels.noPlan', 'No plan'),
          role: profile.role || t('settings.labels.notAvailable'),
        });

        setFormData({
          firstName: profile.first_name || '',
          lastName: profile.last_name || '',
          phoneNumber: profile.phone_number || '',
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
        });
      } catch (err: any) {
        const errorMessage = err.message || t('settings.errors.fetchProfileFailed');
        setError(errorMessage);
        toast.error(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchProfileData();
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev: FormData) => ({ ...prev, [name]: value }));
  };

  const toggleEditMode = (field: keyof EditMode) => {
    setEditMode((prev: EditMode) => ({ ...prev, [field]: !prev[field] }));
    if (editMode[field]) {
      setFormData((prev: FormData) => ({
        ...prev,
        firstName: field === 'name' ? userData.firstName : prev.firstName,
        lastName: field === 'name' ? userData.lastName : prev.lastName,
        phoneNumber: field === 'phone' ? userData.phoneNumber : prev.phoneNumber,
      }));
    }
  };

  const handleNameUpdate = async () => {
    try {
      await profileService.updateName(
        formData.firstName || userData.firstName,
        formData.lastName || userData.lastName
      );

      const newFullName = `${formData.firstName || userData.firstName} ${
        formData.lastName || userData.lastName
      }`.trim();
      setUserData((prev: UserData) => ({
        ...prev,
        fullName: newFullName,
        firstName: formData.firstName || prev.firstName,
        lastName: formData.lastName || prev.lastName,
      }));
      setEditMode((prev: EditMode) => ({ ...prev, name: false }));
      toast.success(t('settings.messages.nameUpdatedSuccess'));
    } catch (err: any) {
      toast.error(t('settings.errors.nameUpdateFailed'));
    }
  };

  const handlePhoneUpdate = async () => {
    try {
      await profileService.updatePhoneNumber(formData.phoneNumber || userData.phoneNumber);

      setUserData((prev: UserData) => ({
        ...prev,
        phoneNumber: formData.phoneNumber || prev.phoneNumber,
        isPhoneVerified: false,
      }));
      setEditMode((prev: EditMode) => ({ ...prev, phone: false }));
      toast.success(t('settings.messages.phoneUpdatedSuccess'));
    } catch (err: any) {
      toast.error(t('settings.errors.phoneUpdateFailed'));
    }
  };

  const requestPhoneOTP = async () => {
    try {
      setShowPhoneOTPModal(true);
      toast.success(t('settings.messages.otpSentPhone'));
    } catch (err: any) {
      toast.error(t('settings.errors.otpSendFailed'));
    }
  };

  const verifyPhoneOTP = async (otp: string) => {
    try {
      await profileService.verifyPhoneNumber(otp);
      setUserData((prev: UserData) => ({ ...prev, isPhoneVerified: true }));
      setShowPhoneOTPModal(false);
      toast.success(t('settings.messages.phoneVerifiedSuccess'));
    } catch (err: any) {
      toast.error(t('settings.errors.phoneVerifyFailed'));
    }
  };

  const requestEmailOTP = async () => {
    try {
      setIsRequestingEmailOTP(true);
      await authService.requestEmailOTP();
      setShowOTPModal(true);
      toast.success(t('settings.messages.otpSentEmail'));
    } catch (err: any) {
      toast.error(t('settings.errors.otpSendFailed'));
    } finally {
      setIsRequestingEmailOTP(false);
    }
  };

  const verifyEmailOTP = async (otp: string) => {
    try {
      await authService.verifyOTP(userData.id, otp);
      setUserData((prev: UserData) => ({ ...prev, isEmailVerified: true }));
      setShowOTPModal(false);
      toast.success(t('settings.messages.emailVerifiedSuccess'));
    } catch (err: any) {
      toast.error(t('settings.errors.emailVerifyFailed') + ': ' + (err.response?.data?.message || err.message));
    }
  };

  const handlePasswordUpdate = async () => {
    if (formData.newPassword !== formData.confirmPassword) {
      toast.error(t('settings.errors.passwordsDoNotMatch'));
      return;
    }

    try {
      await profileService.updatePassword(formData.currentPassword, formData.newPassword);

      setEditMode((prev: EditMode) => ({ ...prev, password: false }));
      setFormData((prev: FormData) => ({
        ...prev,
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      }));
      toast.success(t('settings.messages.passwordUpdatedSuccess'));
      clearAccessToken();
      clearRefreshToken();
      window.location.href = '/login';
    } catch (err: any) {
      toast.error(t('settings.errors.passwordUpdateFailed'));
    }
  };

  const handleLanguageChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newLanguageCode = e.target.value;
    const languageMap: { [key: string]: string } = {
      en: t('settings.labels.english'),
      fr: t('settings.labels.french'),
    };

    try {
      await i18n.changeLanguage(newLanguageCode);
      await profileService.setPreferredLanguage(newLanguageCode);

      setUserData((prev: UserData) => ({
        ...prev,
        preferredLanguage: languageMap[newLanguageCode] || newLanguageCode
      }));

      toast.success(t('settings.messages.languageUpdatedSuccess'));
    } catch (err: any) {
      toast.error(t('settings.errors.languageUpdateFailed'));
    }
  };

  const handle2FAToggle = async () => {
    try {
      if (!userData.is2FAEnabled) {
        await profileService.enableTwoFactorAuth();
        toast.success(t('settings.messages.twoFactorEnabled'));
      } else {
        await profileService.disableTwoFactorAuth();
        toast.success(t('settings.messages.twoFactorDisabled'));
      }
      setUserData((prev: UserData) => ({ ...prev, is2FAEnabled: !prev.is2FAEnabled }));
    } catch (err: any) {
      toast.error(t('settings.errors.twoFactorUpdateFailed'));
    }
  };

  const handleProfilePictureUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const options = {
        maxSizeMB: 1,
        maxWidthOrHeight: 800,
        useWebWorker: true,
      };
      const compressedFile = await imageCompression(file, options);

      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64Image = reader.result as string;

        if (base64Image.length > 5 * 1024 * 1024) {
          toast.error(t('settings.errors.imageTooLarge'));
          return;
        }

        const response: UploadPictureResponse = await profileService.uploadProfilePicture(base64Image);
        const imageData = response.data.data?.imageData;
        setUserData((prev: UserData) => ({
          ...prev,
          profilePicture: imageData || base64Image,
        }));

        toast.success(t('settings.messages.profilePictureUpdatedSuccess'));
      };
      reader.readAsDataURL(compressedFile);
    } catch (err: any) {
      toast.error(t('settings.errors.imageProcessFailed'));
    }
  };

  const handleDeleteAccountClick = async () => {
    setIsDeleting(true);
    try {
      const response = await authService.validateAuth();
      const { daysRemaining, planName } = response.data;

      if (daysRemaining > 0) {
        setSubscriptionInfo({ daysRemaining, planName });
        setShowDeleteModal(false);
        setShowFinalConfirmModal(true);
        setIsDeleting(false);
      } else {
        await performDeleteAccount();
      }
    } catch (err: any) {
      setIsDeleting(false);
      toast.error(t('settings.errors.accountDeleteFailed') || 'Failed to delete account');
    }
  };

  const performDeleteAccount = async () => {
    setIsDeleting(true);
    try {
      await profileService.deleteAccount();
      toast.success(t('settings.messages.accountDeletedSuccess') || 'Account deleted successfully');
      clearAccessToken();
      clearRefreshToken();
      window.location.href = '/login';
    } catch (err: any) {
      setIsDeleting(false);
      toast.error(t('settings.errors.accountDeleteFailed') || 'Failed to delete account');
    }
  };

  // Theme mode options
  const themeModes: { mode: ThemeMode; icon: React.ReactNode; label: string; description: string }[] = [
    {
      mode: 'light',
      icon: <Sun size={20} />,
      label: t('settings.preferences.themeLight', 'Light'),
      description: t('settings.preferences.themeLightDesc', 'Always use light theme'),
    },
    {
      mode: 'dark',
      icon: <Moon size={20} />,
      label: t('settings.preferences.themeDark', 'Dark'),
      description: t('settings.preferences.themeDarkDesc', 'Always use dark theme'),
    },
    {
      mode: 'system',
      icon: <Monitor size={20} />,
      label: t('settings.preferences.themeSystem', 'System'),
      description: t('settings.preferences.themeSystemDesc', 'Match your device settings'),
    },
  ];

  // Shared input style
  const inputStyle: React.CSSProperties = {
    padding: '8px 12px',
    background: inputBg,
    border: `1px solid ${inputBorder}`,
    borderRadius: '8px',
    fontSize: '14px',
    lineHeight: '20px',
    color: inputColor,
    outline: 'none',
  };

  // Shared button styles
  const primaryBtnStyle: React.CSSProperties = {
    padding: '8px 16px',
    background: 'linear-gradient(90deg, #2B7FFF 0%, #AD46FF 100%)',
    borderRadius: '8px',
    border: 'none',
    fontSize: '14px',
    lineHeight: '20px',
    fontWeight: 500,
    color: '#FFFFFF',
    cursor: 'pointer',
  };

  const secondaryBtnStyle: React.CSSProperties = {
    padding: '8px 16px',
    background: isDark ? '#0F172B' : '#F8FAFC',
    border: `1px solid ${cardBorder}`,
    borderRadius: '8px',
    fontSize: '14px',
    lineHeight: '20px',
    fontWeight: 500,
    color: titleColor,
    cursor: 'pointer',
  };

  if (loading) {
    return <Spinner size="large" text={t('settings.loading.settingsData')} fullPage />;
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-20" style={{ background: containerBg }}>
        <AlertTriangle size={32} color="#EF4444" style={{ marginBottom: '8px' }} />
        <h3 style={{ fontSize: '16px', fontWeight: 600, color: titleColor, marginBottom: '4px' }}>{t('settings.errors.loadingSettings')}</h3>
        <p style={{ fontSize: '14px', color: subtitleColor, marginBottom: '12px' }}>{error}</p>
        <button style={primaryBtnStyle} onClick={() => window.location.reload()}>
          {t('settings.buttons.retry')}
        </button>
      </div>
    );
  }

  // Render a section card
  const renderSectionCard = (
    icon: React.ReactNode,
    iconBg: string,
    iconColor: string,
    title: string,
    children: React.ReactNode,
    dangerZone?: boolean
  ) => (
    <div
      className="flex flex-col"
      style={{
        background: cardBg,
        border: `1px solid ${dangerZone ? dangerBorder : cardBorder}`,
        borderRadius: '8px',
        overflow: 'hidden',
      }}
    >
      <div
        className="flex items-center"
        style={{
          padding: '12px 16px',
          gap: '8px',
          borderBottom: `1px solid ${dangerZone ? dangerBorder : cardBorder}`,
          background: dangerZone ? dangerHeaderBg : sectionHeaderBg,
        }}
      >
        <div
          className="flex items-center justify-center flex-shrink-0"
          style={{
            width: '28px',
            height: '28px',
            borderRadius: '8px',
            background: iconBg,
            color: iconColor,
          }}
        >
          {icon}
        </div>
        <span style={{ fontSize: '14px', lineHeight: '20px', fontWeight: 600, color: dangerZone ? '#EF4444' : titleColor }}>
          {title}
        </span>
      </div>
      <div className="flex flex-col" style={{ padding: '16px', gap: '16px' }}>
        {children}
      </div>
    </div>
  );

  // Render a field row
  const renderFieldRow = (label: string, children: React.ReactNode) => (
    <div className="flex flex-col" style={{ gap: '6px' }}>
      <span style={{ fontSize: '12px', lineHeight: '16px', fontWeight: 500, color: subtitleColor }}>{label}</span>
      {children}
    </div>
  );

  // Render a status badge
  const renderBadge = (verified: boolean, enabledLabel?: string, disabledLabel?: string) => {
    if (verified) {
      return (
        <span
          className="inline-flex items-center"
          style={{
            padding: '2px 8px',
            borderRadius: '999px',
            fontSize: '12px',
            lineHeight: '16px',
            fontWeight: 500,
            background: greenBadgeBg,
            color: greenBadgeColor,
            gap: '4px',
          }}
        >
          <CheckCircle size={12} />
          {enabledLabel || t('settings.labels.verified')}
        </span>
      );
    }
    return (
      <span
        className="inline-flex items-center"
        style={{
          padding: '2px 8px',
          borderRadius: '999px',
          fontSize: '12px',
          lineHeight: '16px',
          fontWeight: 500,
          background: amberBadgeBg,
          color: amberBadgeColor,
          gap: '4px',
        }}
      >
        <Clock size={12} />
        {disabledLabel || t('settings.labels.pending')}
      </span>
    );
  };

  return (
    <div
      className="flex flex-col items-start flex-1 min-h-0 font-geist p-0 lg:pt-2 lg:pr-2 lg:pb-2 lg:pl-0"
      style={{
        gap: '8px',
        background: containerBg,
      }}
    >
      {/* Main content wrapper */}
      <div
        className="flex flex-col flex-1 w-full overflow-hidden rounded-none lg:rounded-2xl"
        style={{
          background: mainBg,
          boxShadow: mainShadow,
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'flex-start',
            width: '100%',
            flexShrink: 0,
            padding: '12px 16px',
            borderBottom: `1px solid ${headerBorder}`,
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              width: '100%',
              gap: '8px',
              height: '36px',
            }}
          >
            <button
              className="lg:hidden flex items-center justify-start flex-shrink-0"
              style={{ width: '24px', height: '36px', background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }}
              onClick={toggleSidebar}
            >
              <Menu size={24} color={isDark ? '#E2E8F0' : '#314158'} />
            </button>
            <div
              className="hidden lg:flex"
              style={{
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                width: '36px',
                height: '36px',
                borderRadius: '12px',
                background: isDark ? '#1C398E' : '#EFF6FF',
              }}
            >
              <SettingsIcon size={24} color={isDark ? '#51A2FF' : '#2B7FFF'} />
            </div>
            <h1
              style={{
                fontFamily: 'Geist, sans-serif',
                fontWeight: 700,
                fontSize: '20px',
                lineHeight: '28px',
                color: titleColor,
                margin: 0,
                flex: 1,
              }}
            >
              {t('navigation.settings', 'Settings')}
            </h1>
          </div>
        </div>

        {/* Scrollable content */}
        <div
          className={isDark ? 'scrollbar-themed-dark' : 'scrollbar-themed-light'}
          style={{ flex: 1, overflowY: 'auto', padding: '16px' }}
        >
          <div className="flex flex-col" style={{ gap: '24px' }}>
            {/* Profile Header Card */}
            <div
              className="flex flex-col sm:flex-row sm:items-center"
              style={{
                padding: '16px',
                gap: '16px',
                background: cardBg,
                border: `1px solid ${cardBorder}`,
                borderRadius: '8px',
              }}
            >
              <label htmlFor="profile-picture-upload" className="relative group cursor-pointer flex-shrink-0 self-center sm:self-auto">
                <img
                  src={userData.profilePicture || avatar}
                  alt="Profile"
                  className="object-cover"
                  style={{
                    width: '64px',
                    height: '64px',
                    borderRadius: '50%',
                    border: `2px solid ${cardBorder}`,
                  }}
                />
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleProfilePictureUpload}
                  id="profile-picture-upload"
                  className="absolute inset-0 w-full h-full opacity-0 z-10"
                  style={{ cursor: 'pointer' }}
                />
                <div className="absolute inset-0 flex items-center justify-center rounded-full opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" style={{ background: 'rgba(0,0,0,0.4)' }}>
                  <Camera size={16} color="#FFFFFF" />
                </div>
              </label>
              <div className="flex-1 min-w-0 text-center sm:text-left">
                <h2 className="truncate" style={{ fontSize: '16px', lineHeight: '24px', fontWeight: 600, color: titleColor, margin: 0 }}>
                  {userData.fullName || t('settings.labels.user')}
                </h2>
                <p className="truncate" style={{ fontSize: '14px', lineHeight: '20px', color: subtitleColor, margin: '2px 0 8px' }}>
                  {userData.email}
                </p>
                <div className="flex flex-wrap justify-center sm:justify-start" style={{ gap: '6px' }}>
                  <span
                    style={{
                      padding: '2px 8px',
                      borderRadius: '999px',
                      fontSize: '12px',
                      lineHeight: '16px',
                      fontWeight: 500,
                      background: badgeBg,
                      color: badgeColor,
                    }}
                  >
                    {userData.role || t('settings.labels.user')}
                  </span>
                  <span
                    style={{
                      padding: '2px 8px',
                      borderRadius: '999px',
                      fontSize: '12px',
                      lineHeight: '16px',
                      fontWeight: 500,
                      background: purpleBadgeBg,
                      color: purpleBadgeColor,
                    }}
                  >
                    {userData.subscription || t('settings.labels.free')}
                  </span>
                  {userData.isEmailVerified && renderBadge(true)}
                </div>
              </div>
            </div>

            {/* Two-column grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3" style={{ gap: '24px' }}>
              {/* Left Column */}
              <div className="lg:col-span-2 flex flex-col" style={{ gap: '24px' }}>
                {/* Personal Information */}
                {renderSectionCard(
                  <User size={16} />,
                  badgeBg,
                  badgeColor,
                  t('settings.personalInfo.title'),
                  <>
                    {/* Name */}
                    {renderFieldRow(t('settings.personalInfo.fullName'), editMode.name ? (
                      <div className="flex flex-col" style={{ gap: '8px' }}>
                        <div className="flex flex-col sm:flex-row" style={{ gap: '8px' }}>
                          <input
                            type="text"
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleInputChange}
                            placeholder={t('settings.placeholders.firstName')}
                            className="flex-1"
                            style={inputStyle}
                          />
                          <input
                            type="text"
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleInputChange}
                            placeholder={t('settings.placeholders.lastName')}
                            className="flex-1"
                            style={inputStyle}
                          />
                        </div>
                        <div className="flex" style={{ gap: '8px' }}>
                          <button
                            onClick={handleNameUpdate}
                            className="flex items-center transition-opacity hover:opacity-90"
                            style={{ ...primaryBtnStyle, gap: '4px' }}
                          >
                            <Check size={16} />
                            {t('settings.buttons.save')}
                          </button>
                          <button
                            onClick={() => toggleEditMode('name')}
                            className="flex items-center transition-opacity hover:opacity-80"
                            style={{ ...secondaryBtnStyle, gap: '4px' }}
                          >
                            <X size={16} />
                            {t('settings.buttons.cancel')}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-col sm:flex-row" style={{ gap: '8px' }}>
                        <input
                          type="text"
                          value={userData.fullName}
                          readOnly
                          className="flex-1"
                          style={{ ...inputStyle, cursor: 'default' }}
                        />
                        <button
                          onClick={() => toggleEditMode('name')}
                          className="flex items-center transition-opacity hover:opacity-90"
                          style={{ ...primaryBtnStyle, gap: '4px' }}
                        >
                          <Pencil size={14} />
                          {t('settings.buttons.edit')}
                        </button>
                      </div>
                    ))}

                    {/* Email */}
                    {renderFieldRow(t('settings.personalInfo.emailAddress'), (
                      <div className="flex flex-col sm:flex-row" style={{ gap: '8px' }}>
                        <input
                          type="email"
                          value={userData.email}
                          readOnly
                          className="flex-1 min-w-0"
                          style={{ ...inputStyle, cursor: 'default' }}
                        />
                        {userData.isEmailVerified ? (
                          renderBadge(true)
                        ) : (
                          <button
                            onClick={requestEmailOTP}
                            disabled={isRequestingEmailOTP}
                            className="flex items-center transition-opacity hover:opacity-90"
                            style={{ ...primaryBtnStyle, opacity: isRequestingEmailOTP ? 0.5 : 1 }}
                          >
                            {isRequestingEmailOTP ? <Spinner size="small" color="white" /> : t('settings.buttons.verify')}
                          </button>
                        )}
                      </div>
                    ))}

                    {/* Phone */}
                    {renderFieldRow(t('settings.personalInfo.phoneNumber'), editMode.phone ? (
                      <div className="flex flex-col" style={{ gap: '8px' }}>
                        <input
                          type="tel"
                          name="phoneNumber"
                          value={formData.phoneNumber}
                          onChange={handleInputChange}
                          placeholder={t('settings.placeholders.phoneNumber')}
                          style={inputStyle}
                        />
                        <div className="flex" style={{ gap: '8px' }}>
                          <button
                            onClick={handlePhoneUpdate}
                            className="flex items-center transition-opacity hover:opacity-90"
                            style={{ ...primaryBtnStyle, gap: '4px' }}
                          >
                            <Check size={16} />
                            {t('settings.buttons.save')}
                          </button>
                          <button
                            onClick={() => toggleEditMode('phone')}
                            className="flex items-center transition-opacity hover:opacity-80"
                            style={{ ...secondaryBtnStyle, gap: '4px' }}
                          >
                            <X size={16} />
                            {t('settings.buttons.cancel')}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-col" style={{ gap: '8px' }}>
                        <div className="flex flex-col sm:flex-row" style={{ gap: '8px' }}>
                          <input
                            type="tel"
                            value={userData.phoneNumber || t('settings.labels.notSet')}
                            readOnly
                            className="flex-1 min-w-0"
                            style={{ ...inputStyle, cursor: 'default' }}
                          />
                          {userData.phoneNumber && userData.isPhoneVerified && renderBadge(true)}
                        </div>
                        <div className="flex flex-col sm:flex-row" style={{ gap: '8px' }}>
                          {userData.phoneNumber && !userData.isPhoneVerified && (
                            <button
                              onClick={requestPhoneOTP}
                              className="transition-opacity hover:opacity-90"
                              style={{
                                ...secondaryBtnStyle,
                                background: amberBadgeBg,
                                color: amberBadgeColor,
                                border: `1px solid ${amberBadgeColor}`,
                              }}
                            >
                              {t('settings.buttons.verify')}
                            </button>
                          )}
                          <button
                            onClick={() => toggleEditMode('phone')}
                            className="flex items-center transition-opacity hover:opacity-90"
                            style={{ ...primaryBtnStyle, gap: '4px' }}
                          >
                            <Pencil size={14} />
                            {userData.phoneNumber ? t('settings.buttons.edit') : t('settings.buttons.add')}
                          </button>
                        </div>
                      </div>
                    ))}
                  </>
                )}

                {/* Preferences */}
                {renderSectionCard(
                  <Globe size={16} />,
                  purpleBadgeBg,
                  purpleBadgeColor,
                  t('settings.preferences.title'),
                  <>
                    {/* Theme Selection */}
                    {renderFieldRow(t('settings.preferences.theme', 'Theme'), (
                      <div className="grid grid-cols-1 sm:grid-cols-3" style={{ gap: '8px' }}>
                        {themeModes.map((tm) => (
                          <button
                            key={tm.mode}
                            onClick={() => setThemeMode(tm.mode)}
                            className="flex flex-col items-center transition-all"
                            style={{
                              padding: '12px 8px',
                              gap: '6px',
                              borderRadius: '8px',
                              border: `2px solid ${themeMode === tm.mode
                                ? (isDark ? '#2B7FFF' : '#2B7FFF')
                                : cardBorder}`,
                              background: themeMode === tm.mode
                                ? (isDark ? '#162456' : '#EFF6FF')
                                : cardBg,
                              cursor: 'pointer',
                              color: themeMode === tm.mode ? badgeColor : subtitleColor,
                            }}
                          >
                            {tm.icon}
                            <span style={{ fontSize: '13px', lineHeight: '18px', fontWeight: 500 }}>
                              {tm.label}
                            </span>
                            <span style={{ fontSize: '11px', lineHeight: '14px', fontWeight: 400, color: subtitleColor }}>
                              {tm.description}
                            </span>
                          </button>
                        ))}
                      </div>
                    ))}

                    {/* Language Selection */}
                    {renderFieldRow(t('settings.preferences.language'), (
                      <div className="flex flex-col" style={{ gap: '4px' }}>
                        <select
                          value={i18n.language || 'en'}
                          onChange={handleLanguageChange}
                          style={{
                            ...inputStyle,
                            cursor: 'pointer',
                            appearance: 'auto' as any,
                          }}
                        >
                          <option value="en">{t('settings.labels.english')}</option>
                          <option value="fr">{t('settings.labels.french')}</option>
                        </select>
                        <span style={{ fontSize: '12px', lineHeight: '16px', color: subtitleColor }}>
                          {t('settings.preferences.languageHelper')}
                        </span>
                      </div>
                    ))}
                  </>
                )}

                {/* Danger Zone */}
                {renderSectionCard(
                  <Trash2 size={16} />,
                  dangerBg,
                  '#EF4444',
                  t('settings.dangerZone.title') || 'Delete',
                  <>
                    <p style={{ fontSize: '14px', lineHeight: '20px', color: subtitleColor }}>
                      {t('settings.dangerZone.deleteDescription') || 'Deleting your account will schedule the deletion of all your data. You can reactivate your account at any time before permanent deletion.'}
                    </p>
                    <button
                      onClick={() => setShowDeleteModal(true)}
                      className="flex items-center transition-opacity hover:opacity-90 self-start"
                      style={{
                        padding: '8px 16px',
                        background: '#EF4444',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '14px',
                        lineHeight: '20px',
                        fontWeight: 500,
                        color: '#FFFFFF',
                        cursor: 'pointer',
                        gap: '4px',
                      }}
                    >
                      <Trash2 size={14} />
                      {t('settings.buttons.deleteAccount') || 'Delete Account'}
                    </button>
                  </>,
                  true
                )}
              </div>

              {/* Right Column */}
              <div className="flex flex-col" style={{ gap: '24px' }}>
                {/* Account Information */}
                {renderSectionCard(
                  <Info size={16} />,
                  badgeBg,
                  badgeColor,
                  t('settings.accountInfo.title'),
                  <>
                    <div className="flex items-center justify-between" style={{ paddingBottom: '8px', borderBottom: `1px solid ${cardBorder}` }}>
                      <span style={{ fontSize: '14px', color: subtitleColor }}>{t('settings.accountInfo.role')}</span>
                      <span style={{ fontSize: '14px', fontWeight: 500, color: titleColor }}>{userData.role || t('settings.labels.user')}</span>
                    </div>
                    <div className="flex items-center justify-between" style={{ paddingBottom: '8px', borderBottom: `1px solid ${cardBorder}` }}>
                      <span style={{ fontSize: '14px', color: subtitleColor }}>{t('settings.accountInfo.plan')}</span>
                      <span style={{ fontSize: '14px', fontWeight: 500, color: titleColor }}>{userData.subscription || t('settings.labels.free')}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span style={{ fontSize: '14px', color: subtitleColor }}>{t('settings.accountInfo.language')}</span>
                      <span style={{ fontSize: '14px', fontWeight: 500, color: titleColor }}>{userData.preferredLanguage}</span>
                    </div>
                  </>
                )}

                {/* Security Status */}
                {renderSectionCard(
                  <Shield size={16} />,
                  greenBadgeBg,
                  greenBadgeColor,
                  t('settings.security.title'),
                  <>
                    <div className="flex items-center justify-between">
                      <span style={{ fontSize: '14px', color: subtitleColor }}>{t('settings.security.email')}</span>
                      {renderBadge(userData.isEmailVerified)}
                    </div>
                    <div className="flex items-center justify-between">
                      <span style={{ fontSize: '14px', color: subtitleColor }}>{t('settings.security.phone')}</span>
                      {renderBadge(userData.isPhoneVerified)}
                    </div>
                    <div className="flex items-center justify-between">
                      <span style={{ fontSize: '14px', color: subtitleColor }}>{t('settings.security.twoFactor')}</span>
                      {userData.is2FAEnabled ? (
                        renderBadge(true, t('settings.labels.enabled'))
                      ) : (
                        <span
                          className="inline-flex items-center"
                          style={{
                            padding: '2px 8px',
                            borderRadius: '999px',
                            fontSize: '12px',
                            lineHeight: '16px',
                            fontWeight: 500,
                            background: isDark ? '#1D293D' : '#F1F5F9',
                            color: subtitleColor,
                            gap: '4px',
                          }}
                        >
                          <X size={12} />
                          {t('settings.labels.disabled')}
                        </span>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Account Modal — portaled to body so it covers sidebar */}
      {showDeleteModal && ReactDOM.createPortal(
        <div
          style={{
            position: 'fixed',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: '16px',
            background: isDark ? 'rgba(2, 6, 24, 0.75)' : 'rgba(241, 245, 249, 0.75)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
          }}
          onMouseDown={(e) => { if (e.target === e.currentTarget) setShowDeleteModal(false); }}
        >
          <div style={{ background: modalBg, borderRadius: '16px', maxWidth: '480px', width: '100%', boxShadow: mainShadow, overflow: 'hidden' }}>
            <div className="flex items-center justify-between" style={{ padding: '16px 20px', borderBottom: `1px solid ${modalHeaderBorder}` }}>
              <div className="flex items-center" style={{ gap: '8px' }}>
                <AlertTriangle size={20} color="#EF4444" />
                <span style={{ fontSize: '16px', fontWeight: 600, color: titleColor }}>{t('settings.modals.deleteAccount.title') || 'Delete Account'}</span>
              </div>
              <button onClick={() => setShowDeleteModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: subtitleColor }}>
                <X size={20} />
              </button>
            </div>
            <div style={{ padding: '20px' }}>
              <p style={{ fontSize: '14px', color: subtitleColor, marginBottom: '12px' }}>
                {t('settings.modals.deleteAccount.warning') || 'Are you sure you want to delete your account?'}
              </p>
              <div style={{ background: dangerBg, border: `1px solid ${dangerBorder}`, borderRadius: '8px', padding: '12px', marginBottom: '12px' }}>
                <p style={{ fontSize: '13px', fontWeight: 500, color: titleColor, marginBottom: '8px' }}>{t('settings.modals.deleteAccount.whatWillHappen') || 'What will happen:'}</p>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {[
                    t('settings.modals.deleteAccount.item1') || 'All your account specific information will be permanently deleted',
                    t('settings.modals.deleteAccount.item2') || 'All connected social media accounts will be removed',
                    t('settings.modals.deleteAccount.item3') || 'All automated actions will be stopped immediately',
                  ].map((item, i) => (
                    <li key={i} className="flex items-start" style={{ gap: '8px', marginBottom: '6px', fontSize: '13px', color: subtitleColor }}>
                      <Trash2 size={12} color="#EF4444" style={{ marginTop: '3px', flexShrink: 0 }} />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="flex" style={{ gap: '8px' }}>
                <button
                  onClick={() => setShowDeleteModal(false)}
                  disabled={isDeleting}
                  className="flex-1 transition-opacity hover:opacity-80"
                  style={{ ...secondaryBtnStyle, opacity: isDeleting ? 0.5 : 1 }}
                >
                  {t('settings.buttons.cancel')}
                </button>
                <button
                  onClick={handleDeleteAccountClick}
                  disabled={isDeleting}
                  className="flex-1 transition-opacity hover:opacity-90"
                  style={{
                    padding: '8px 16px',
                    background: '#EF4444',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#FFFFFF',
                    cursor: isDeleting ? 'default' : 'pointer',
                    opacity: isDeleting ? 0.5 : 1,
                  }}
                >
                  {isDeleting ? (t('settings.buttons.deleting') || 'Deleting...') : (t('settings.buttons.deleteAccount') || 'Delete Account')}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Final Confirmation Modal — portaled to body so it covers sidebar */}
      {showFinalConfirmModal && subscriptionInfo && ReactDOM.createPortal(
        <div
          style={{
            position: 'fixed',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: '16px',
            background: isDark ? 'rgba(2, 6, 24, 0.75)' : 'rgba(241, 245, 249, 0.75)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
          }}
          onMouseDown={(e) => { if (e.target === e.currentTarget) setShowFinalConfirmModal(false); }}
        >
          <div style={{ background: modalBg, borderRadius: '16px', maxWidth: '480px', width: '100%', boxShadow: mainShadow, overflow: 'hidden' }}>
            <div className="flex items-center justify-between" style={{ padding: '16px 20px', borderBottom: `1px solid ${modalHeaderBorder}`, background: dangerHeaderBg }}>
              <div className="flex items-center" style={{ gap: '8px' }}>
                <AlertTriangle size={20} color="#EF4444" />
                <span style={{ fontSize: '16px', fontWeight: 600, color: '#EF4444' }}>{t('settings.modals.finalConfirm.title') || 'Final Confirmation'}</span>
              </div>
              <button onClick={() => setShowFinalConfirmModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: subtitleColor }}>
                <X size={20} />
              </button>
            </div>
            <div style={{ padding: '20px' }}>
              <div style={{ background: dangerBg, border: `1px solid ${dangerBorder}`, borderRadius: '8px', padding: '16px', marginBottom: '16px' }}>
                <p style={{ fontSize: '14px', fontWeight: 600, color: titleColor, marginBottom: '4px' }}>
                  {t('settings.modals.finalConfirm.activeSubscription') || 'You have an active subscription!'}
                </p>
                <p style={{ fontSize: '13px', color: subtitleColor, marginBottom: '8px' }}>
                  {t('settings.modals.finalConfirm.planInfo', { plan: subscriptionInfo.planName, days: subscriptionInfo.daysRemaining }) ||
                    `${subscriptionInfo.planName} - ${subscriptionInfo.daysRemaining} days remaining`}
                </p>
                <p style={{ fontSize: '13px', fontWeight: 500, color: '#EF4444' }}>
                  {t('settings.modals.finalConfirm.warningMessage') || 'Deleting your account will forfeit your remaining subscription time. This cannot be recovered.'}
                </p>
              </div>
              <p style={{ fontSize: '14px', color: subtitleColor, textAlign: 'center', marginBottom: '16px', fontWeight: 500 }}>
                {t('settings.modals.finalConfirm.confirmQuestion') || 'Are you absolutely sure you want to proceed?'}
              </p>
              <div className="flex" style={{ gap: '8px' }}>
                <button
                  onClick={() => setShowFinalConfirmModal(false)}
                  disabled={isDeleting}
                  className="flex-1 transition-opacity hover:opacity-80"
                  style={{ ...secondaryBtnStyle, opacity: isDeleting ? 0.5 : 1 }}
                >
                  {t('settings.buttons.cancel') || 'Cancel'}
                </button>
                <button
                  onClick={performDeleteAccount}
                  disabled={isDeleting}
                  className="flex-1 transition-opacity hover:opacity-90"
                  style={{
                    padding: '8px 16px',
                    background: '#DC2626',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#FFFFFF',
                    cursor: isDeleting ? 'default' : 'pointer',
                    opacity: isDeleting ? 0.5 : 1,
                  }}
                >
                  {isDeleting ? (t('settings.buttons.deleting') || 'Deleting...') : (t('settings.modals.finalConfirm.confirmDelete') || 'Yes, Delete Account')}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Email OTP Modal */}
      <OTPModal
        isOpen={showOTPModal}
        onClose={() => setShowOTPModal(false)}
        onConfirm={verifyEmailOTP}
        title={t('settings.modals.verifyEmail.title')}
        message={t('settings.modals.verifyEmail.message', { email: userData.email })}
        confirmText={t('settings.modals.verifyEmail.confirmButton')}
      />

      {/* Phone OTP Modal — portaled to body so it covers sidebar */}
      {showPhoneOTPModal && ReactDOM.createPortal(
        <div
          style={{
            position: 'fixed',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: '16px',
            background: isDark ? 'rgba(2, 6, 24, 0.75)' : 'rgba(241, 245, 249, 0.75)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
          }}
          onMouseDown={(e) => { if (e.target === e.currentTarget) setShowPhoneOTPModal(false); }}
        >
          <div style={{ background: modalBg, borderRadius: '16px', maxWidth: '480px', width: '100%', boxShadow: mainShadow, overflow: 'hidden' }}>
            <div className="flex items-center justify-between" style={{ padding: '16px 20px', borderBottom: `1px solid ${modalHeaderBorder}` }}>
              <div className="flex items-center" style={{ gap: '8px' }}>
                <Info size={20} color={badgeColor} />
                <span style={{ fontSize: '16px', fontWeight: 600, color: titleColor }}>{t('settings.modals.verifyPhone.title')}</span>
              </div>
              <button onClick={() => setShowPhoneOTPModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: subtitleColor }}>
                <X size={20} />
              </button>
            </div>
            <div style={{ padding: '20px' }}>
              <div style={{ background: badgeBg, border: `1px solid ${isDark ? '#1C398E' : '#BFDBFE'}`, borderRadius: '8px', padding: '12px', marginBottom: '16px' }}>
                <p style={{ fontSize: '13px', color: subtitleColor, marginBottom: '4px' }}>
                  {t('settings.modals.verifyPhone.codeSent')}
                </p>
                <p style={{ fontSize: '14px', fontWeight: 600, color: titleColor }}>{userData.phoneNumber}</p>
              </div>
              <form onSubmit={(e) => {
                e.preventDefault();
                const otp = (e.target as HTMLFormElement).otp.value;
                if (otp.trim()) verifyPhoneOTP(otp);
              }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 500, color: subtitleColor, marginBottom: '8px' }}>
                  {t('settings.modals.verifyPhone.enterCode')}
                </label>
                <input
                  type="text"
                  name="otp"
                  placeholder={t('settings.placeholders.otpCode')}
                  maxLength={6}
                  autoFocus
                  style={{
                    ...inputStyle,
                    width: '100%',
                    textAlign: 'center',
                    fontSize: '18px',
                    fontWeight: 600,
                    letterSpacing: '0.2em',
                    padding: '10px 12px',
                  }}
                />
                <div className="flex items-center justify-center" style={{ gap: '4px', marginTop: '8px' }}>
                  <Clock size={12} color={subtitleColor} />
                  <span style={{ fontSize: '12px', color: subtitleColor }}>{t('settings.modals.verifyPhone.codeExpires')}</span>
                </div>
                <div className="flex" style={{ gap: '8px', marginTop: '16px' }}>
                  <button
                    type="button"
                    onClick={() => setShowPhoneOTPModal(false)}
                    className="flex-1 transition-opacity hover:opacity-80"
                    style={secondaryBtnStyle}
                  >
                    {t('settings.buttons.cancel')}
                  </button>
                  <button
                    type="submit"
                    className="flex-1 transition-opacity hover:opacity-90"
                    style={primaryBtnStyle}
                  >
                    {t('settings.modals.verifyPhone.confirmButton')}
                  </button>
                </div>
              </form>
              <div style={{ textAlign: 'center', marginTop: '12px' }}>
                <button
                  onClick={requestPhoneOTP}
                  style={{ background: 'none', border: 'none', fontSize: '13px', color: badgeColor, fontWeight: 500, cursor: 'pointer' }}
                >
                  {t('settings.modals.verifyPhone.resendCode')}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default Settings;
